/**
 * Holdback State — Phase 1c (DERIVED, never persisted)
 * ─────────────────────────────────────────────────────
 * Folds the append-only ledger into per-holdback state. Mirrors the
 * 1b pattern of `isContractorExecutionBlocked` being derived from
 * stored fields rather than persisted as its own truth.
 */

import type { HoldbackLedgerEntry } from "./ledger";

export type HoldbackStatus = "active" | "paused" | "released" | "reversed";

export interface HoldbackState {
  holdbackId: string;
  jobId: string;
  contractorId: string;
  status: HoldbackStatus;
  /** Net amount currently held back (create + resume - release - reverse). */
  netHeld: number;
  /** All ledger entries for this holdback, ordered chronologically. */
  entries: HoldbackLedgerEntry[];
  /** Active dispute pausing release, if any. */
  pausedByDisputeId?: string;
  /** Most recent entry. */
  lastEntryAt: string;
}

function chrono(a: HoldbackLedgerEntry, b: HoldbackLedgerEntry): number {
  return Date.parse(a.createdAt) - Date.parse(b.createdAt);
}

export function deriveHoldbackState(
  ledger: HoldbackLedgerEntry[],
  holdbackId: string,
): HoldbackState | null {
  const entries = ledger.filter((e) => e.holdbackId === holdbackId).sort(chrono);
  if (entries.length === 0) return null;

  const first = entries[0];
  if (first.action !== "create") {
    // malformed — treat as null rather than synthesize state
    return null;
  }

  let net = 0;
  let pausedByDisputeId: string | undefined;
  let totallyReversed = false;

  for (const e of entries) {
    switch (e.action) {
      case "create":
        net += e.amount;
        break;
      case "release":
        net -= e.amount;
        break;
      case "pause":
        pausedByDisputeId = e.pausedByDisputeId;
        break;
      case "resume":
        pausedByDisputeId = undefined;
        break;
      case "reverse": {
        const target = entries.find((x) => x.id === e.reversesEntryId);
        if (!target) break;
        if (target.action === "create") {
          net -= target.amount;
          if (net <= 0) totallyReversed = true;
        } else if (target.action === "release") {
          net += target.amount;
        }
        break;
      }
    }
  }

  let status: HoldbackStatus;
  if (totallyReversed) status = "reversed";
  else if (net <= 0) status = "released";
  else if (pausedByDisputeId) status = "paused";
  else status = "active";

  return {
    holdbackId,
    jobId: first.jobId,
    contractorId: first.contractorId,
    status,
    netHeld: Math.max(0, net),
    entries,
    pausedByDisputeId,
    lastEntryAt: entries[entries.length - 1].createdAt,
  };
}

export function deriveAllHoldbackStates(ledger: HoldbackLedgerEntry[]): HoldbackState[] {
  const ids = Array.from(new Set(ledger.map((e) => e.holdbackId)));
  return ids
    .map((id) => deriveHoldbackState(ledger, id))
    .filter((s): s is HoldbackState => s !== null);
}

export function getActiveHoldbacksForContractor(
  ledger: HoldbackLedgerEntry[],
  contractorId: string,
): HoldbackState[] {
  return deriveAllHoldbackStates(ledger).filter(
    (s) => s.contractorId === contractorId && (s.status === "active" || s.status === "paused"),
  );
}

export function getActiveHoldbacksForJob(
  ledger: HoldbackLedgerEntry[],
  jobId: string,
): HoldbackState[] {
  return deriveAllHoldbackStates(ledger).filter(
    (s) => s.jobId === jobId && (s.status === "active" || s.status === "paused"),
  );
}
